// Only reviewed provider directives are hidden; bracketed business data stays literal.
const SPEECH_DIRECTIVES = new Set([
  "laughs",
  "laughing",
  "chuckles",
  "whispers",
  "sighs",
  "exhales",
  "sarcastic",
  "curious",
  "excited",
  "crying",
  "snorts",
  "mischievously",
  "short pause",
  "long pause",
  "slow",
]);

export function readableSpeechText(text: string): string {
  // Protect literal code and quotations before recognizing delivery directives.
  return text.replace(
    /(`+)[\s\S]*?(?:\1(?!`)|$)|(~{3,})[\s\S]*?(?:\2(?!~)|$)|"(?:\\.|[^"\\\n])*"|(?<!\w)'(?:\\.|[^'\\\n])*'(?!\w)|“[^”\n]*”|‘[^’\n]*’|(?:^|\n)[ \t]{0,3}>[^\n]*|(?<!\\)\[([^\]\n]+)\](?![([])/g,
    (part, _backticks, _tildes, name: string | undefined) =>
      name && SPEECH_DIRECTIVES.has(name.toLowerCase()) ? "" : part,
  );
}

export interface AudioAlignment {
  chars: string[];
  charStartTimesMs: number[];
  charDurationsMs: number[];
}

export interface SpokenCaption {
  text: string;
  activeStart: number;
  activeEnd: number;
}

export function readAudioAlignment(value: unknown): AudioAlignment | undefined {
  if (!value || typeof value !== "object") return;
  const {
    chars,
    char_start_times_ms: charStartTimesMs,
    char_durations_ms: charDurationsMs,
  } = value as Record<string, unknown>;
  if (
    !Array.isArray(chars) ||
    !chars.length ||
    !chars.every((char) => typeof char === "string" && char.length > 0) ||
    !Array.isArray(charStartTimesMs) ||
    !Array.isArray(charDurationsMs) ||
    chars.length !== charStartTimesMs.length ||
    chars.length !== charDurationsMs.length ||
    !charStartTimesMs.every(
      (time, index) =>
        typeof time === "number" &&
        Number.isFinite(time) &&
        time >= 0 &&
        (index === 0 || time >= charStartTimesMs[index - 1]),
    ) ||
    !charDurationsMs.every((time) => typeof time === "number" && Number.isFinite(time) && time >= 0)
  )
    return;
  return { chars, charStartTimesMs, charDurationsMs };
}

export function captionAt(alignment: AudioAlignment, elapsedMs: number): SpokenCaption | null {
  const index = alignment.chars.findIndex(
    (_, i) =>
      elapsedMs >= alignment.charStartTimesMs[i] &&
      elapsedMs < alignment.charStartTimesMs[i] + alignment.charDurationsMs[i],
  );
  if (index < 0) return null;
  const text = alignment.chars.join("");
  const offset = alignment.chars.slice(0, index).join("").length;
  if (/^\s+$/.test(alignment.chars[index])) return null;
  let activeStart = offset;
  let activeEnd = offset + alignment.chars[index].length;
  while (activeStart > 0 && !/\s/.test(text[activeStart - 1])) activeStart--;
  while (activeEnd < text.length && !/\s/.test(text[activeEnd])) activeEnd++;
  return { text, activeStart, activeEnd };
}
